import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Button,
  Icon,
  Flex,
  HStack,
  Badge,
  useColorModeValue,
  Card,
  CardBody,
  Divider,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Link as RouterLink } from 'react-router-dom'
import {
  ArrowRight,
} from '@phosphor-icons/react'
import { useTexture } from '../../context/EnhancedTextureContext';

const float = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-5px) rotate(1deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const stepIcons = [
  {
    title: 'Discover Sites',
    description:
      'Browse our interactive world map or search for specific megalithic sites by name, region, or features',
  },
  {
    title: 'Document Findings',
    description:
      'Upload high-resolution photos and tag specific features using our standardized classification system',
  },
  {
    title: 'Collaborate',
    description:
      'Discuss findings with other researchers, validate observations, and contribute to the knowledge base',
  },
  {
    title: 'Explore Connections',
    description:
      'Analyze similarities between sites across different regions and time periods',
  },
];

export function HowItWorksSection() {
  const { isTextureEnabled } = useTexture();

  // Define conditional styles for Card texture override
  const cardTextureImage = useColorModeValue(
    'var(--texture-field-guide-light)',
    'var(--texture-field-guide-dark)'
  );
  const cardTextureBlendMode = useColorModeValue('soft-light', 'multiply');
  const cardTextureOpacity = useColorModeValue(0.15, 0.2);

  return (
    <Box
      bg="bg.surface" // This will be the very back background color
      py={{ base: 20, md: 24 }}
      position="relative"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="border.light"
      overflow="hidden" // Important for pseudo-elements not to overflow if sized larger
      _before={{ // Leather texture - moved to outer Box
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? "url('/textures/leather.png')" : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        opacity: isTextureEnabled ? useColorModeValue(0.1, 0.35) : 0,
        mixBlendMode: 'multiply', // Consider changing this if leather is too dark, like in OptimizedFeaturesSection
        pointerEvents: 'none',
        zIndex: 0, // Behind Container, above Box's own bg
        transition: 'opacity 0.3s ease-in-out',
      }}
      _after={{ // Linen texture - moved to outer Box
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? "url('/textures/linen.png')" : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        opacity: isTextureEnabled ? useColorModeValue(0.15, 0) : 0,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
        zIndex: 0, // Behind Container, above Box's own bg
        display: useColorModeValue('block', 'none'),
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <Container
        variant="journal"
        maxW="container.xl"
        bg={useColorModeValue("bg.surface", "bg.surface._dark")} // This bg is for the content area itself
        boxShadow="lg"
        borderRadius="md"
        position="relative"
        zIndex={1} // Ensure container is above the Box's pseudo-elements
        // _before and _after pseudo-elements removed from here
      >
        <VStack spacing={{ base: 12, md: 16 }}>
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center">
            <Box position="relative">
              <Heading 
                variant="journal" 
                size="2xl" 
                position="relative"
                _after={{
                  content: "'Field Guide'",
                  position: "absolute",
                  top: "-15px",
                  right: "-40px",
                  fontFamily: "handwritten",
                  fontSize: "sm",
                  color: "text.muted",
                  transform: "rotate(10deg)",
                  display: { base: 'none', md: 'block' }
                }}
              >
                How It Works
              </Heading>
              
              <Box 
                position="absolute" 
                bottom="-8px" 
                left="50%" 
                transform="translateX(-50%)"
                width="80px"
                height="2px"
                bgGradient="linear(to-r, transparent, accent.highlight, transparent)"
              />
            </Box>
            
            <HStack spacing={2} justifyContent="center" mb={2}>
              <Badge variant="journal">Simple</Badge>
              <Badge variant="journal">Step-by-Step</Badge>
              <Badge variant="journal">Comprehensive</Badge>
            </HStack>
            
            <Text 
              fontSize={{ base: "lg", md: "xl" }}
              maxW="3xl" 
              color="text.muted"
              fontStyle="italic"
              px={{ base: 4, md: 0 }}
              lineHeight="1.7"
            >
              Our platform makes it easy to explore, document, and analyze megalithic sites through a simple workflow.
            </Text>
          </VStack>

          <SimpleGrid 
            columns={{ base: 1, md: 2, lg: 4 }} 
            spacing={{ base: 8, md: 10, lg: 12 }}
            position="relative"
          >
            {stepIcons.map((step, index) => (
              <Card
                key={index}
                variant="journal"
                position="relative"
                zIndex={1}
                overflow="hidden"
                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                animation={`${float} ${6 + index}s infinite ease-in-out`}
                style={{ animationDelay: `${index * 0.5}s` }}
                _hover={{
                  boxShadow: "2xl",
                  transform: "translateY(-8px) scale(1.02)",
                  borderColor: "interactive.primary",
                }}
                cursor="pointer"
                sx={{
                  'body.textures-enabled &': {
                    _before: {
                      opacity: cardTextureOpacity,
                      bgImage: cardTextureImage,
                      bgBlendMode: cardTextureBlendMode,
                      bgSize: '250px',
                      bgRepeat: 'repeat',
                    },
                  },
                  '@media(prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              >
                <CardBody p={{ base: 6, md: 8 }}>
                  <VStack
                    spacing={4}
                    align="center"
                    textAlign="center"
                    w="100%"
                  >
                    <Badge 
                      variant="subtle" 
                      colorScheme="blue"
                      fontSize="xs"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      Step {index + 1}
                    </Badge>
                    
                    <Heading 
                      size={{ base: "md", md: "lg" }}
                      fontFamily="heading"
                      color="text.default"
                      mb={2}
                    >
                      {step.title}
                    </Heading>
                    
                    <Text 
                      color="text.muted" 
                      fontFamily="body"
                      fontSize={{ base: "sm", md: "md" }}
                      lineHeight="1.6"
                      px={{ base: 2, md: 0 }}
                    >
                      {step.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          <Box 
            position="relative" 
            width="100%" 
            textAlign="center"
            pt={{ base: 8, md: 12 }}
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: '25%',
              right: '25%',
              height: '1px',
              bgGradient: 'linear(to-r, transparent, border.default, transparent)',
              opacity: 0.6,
            }}
          >
            <VStack spacing={4}>
              <Text 
                fontSize={{ base: "sm", md: "md" }}
                color="text.muted"
                fontStyle="italic"
              >
                Begin your research with our comprehensive platform.
              </Text>
              <Button
                as={RouterLink}
                to="/about"
                size={{ base: "md", md: "lg" }}
                variant="journal"
                rightIcon={<Icon as={ArrowRight} weight="bold" />}
                position="relative"
                _hover={{
                  transform: "translateY(-3px)",
                  boxShadow: "lg",
                }}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                boxShadow="md"
                px={{ base: 6, md: 8 }}
                py={{ base: 3, md: 4 }}
                fontSize={{ base: "sm", md: "md" }}
                _after={{
                  content: `"Learn more ➡"`,
                  position: "absolute",
                  top: "-18px",
                  right: "-20px",
                  fontFamily: "handwritten",
                  fontSize: "xs",
                  color: "text.muted",
                  transform: "rotate(5deg)",
                  opacity: 0.7,
                  display: { base: 'none', md: 'block' }
                }}
              >
                Learn More About Our Platform
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
